import { Box, Typography, Tooltip, IconButton } from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";

import React, { useMemo, useState, useRef, useEffect } from "react";
import { useBookmakers } from "../contexts/BookmakersContext";
import BetTrackerButton from "./BetTrackerButton";
import { formatRate, getBookieLogoUrl } from "../constants";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const BetDetails = ({ market }) => {
  const { bookmakers, selectedBookmakers } = useBookmakers();
  const [sortedBetDetails, setSortedBetDetails] = useState(null);

  const topBoxRef = useRef(null);
  const bottomBoxRef = useRef(null);

  const isPro = market.level === "Pro";

  useEffect(() => {
    const topBox = topBoxRef.current;
    const bottomBox = bottomBoxRef.current;

    const handleTopScroll = () => {
      if (bottomBox && topBox.scrollTop !== bottomBox.scrollTop) {
        bottomBox.scrollTop = topBox.scrollTop;
      }
    };

    const handleBottomScroll = () => {
      if (topBox && topBox.scrollTop !== bottomBox.scrollTop) {
        topBox.scrollTop = bottomBox.scrollTop;
      }
    };

    if (topBox) {
      topBox.addEventListener("scroll", handleTopScroll);
    }
    if (bottomBox) {
      bottomBox.addEventListener("scroll", handleBottomScroll);
    }

    return () => {
      if (topBox) {
        topBox.removeEventListener("scroll", handleTopScroll);
      }
      if (bottomBox) {
        bottomBox.removeEventListener("scroll", handleBottomScroll);
      }
    };
  }, []);

  const detailsRows = useMemo(() => {
    const filteredGroupedBets = Object.entries(market.groupedBets).map(
      ([key, value]) => ({ ...value, key })
    );

    return filteredGroupedBets;
  }, [market.groupedBets]);

  const uniqueBookies = useMemo(() => {
    const unique = new Set();

    detailsRows.forEach((market) => {
      market.bets.forEach((bet) => {
        unique.add(bet.bookie);
      });
    });

    return Array.from(unique).sort();
  }, [detailsRows]);

  const unselectedBookmakers = useMemo(() => {
    return Object.keys(bookmakers).filter(
      (bookmaker) => !selectedBookmakers.includes(bookmaker)
    );
  }, [bookmakers, selectedBookmakers]);

  const handleSort = (betDetails) => {
    setSortedBetDetails(betDetails);
  };

  const selectedUniqueBookies = useMemo(() => {
    return uniqueBookies.filter((bookie) =>
      selectedBookmakers.includes(bookie)
    );
  }, [uniqueBookies, selectedBookmakers]);

  const unselectedUniqueBookies = useMemo(() => {
    return uniqueBookies.filter((bookie) =>
      unselectedBookmakers.includes(bookie)
    );
  }, [uniqueBookies, unselectedBookmakers]);

  console.log(market);

  const sortBookies = (bookies, sortedBetDetails) => {
    if (!sortedBetDetails) return bookies;

    const sortedBookies = [...bookies];
    sortedBookies.sort((a, b) => {
      const betA = sortedBetDetails.bets.find((bet) => bet.bookie === a);
      const betB = sortedBetDetails.bets.find((bet) => bet.bookie === b);

      if (betA?.rate && !betB?.rate) return -1;
      if (!betA?.rate && betB?.rate) return 1;
      if (!betA?.rate && !betB?.rate) return 0;

      return betB.rate - betA.rate;
    });
    return sortedBookies;
  };

  const sortedSelectedUniqueBookies = useMemo(() => {
    return sortBookies(selectedUniqueBookies, sortedBetDetails);
  }, [selectedUniqueBookies, sortedBetDetails]);

  const sortedUnselectedUniqueBookies = useMemo(() => {
    return sortBookies(unselectedUniqueBookies, sortedBetDetails);
  }, [unselectedUniqueBookies, sortedBetDetails]);

  const getBetTrackerContent = (betForBookie, betDetails) => {
    if (!betForBookie) return "";

    return `${market.date.toISOString().slice(0, 10)}\t${
      betForBookie.bookie === "Neds Group" ? "Neds" : betForBookie.bookie
    }\tMug\t${localStorage.getItem("odds-comparison-sport") || "Soccer"}\t${
      market.eventName
    }\t${market?.market} ${betDetails.key}\t\t${betForBookie.rate}`;
  };

  const renderBookieLogo = (bookie, i, showLogo = true) => {
    const styles = {};
    const textStyles = {};
    if (i === sortedSelectedUniqueBookies?.length - 1) {
      styles.marginRight = "44px";
    }
    if (i === "grey") {
      if (!isPro) return null;
    }
    return (
      <Box key={bookie} sx={{ width: "40px", height: "120px", ...styles }}>
        {showLogo ? (
          <img
            src={getBookieLogoUrl(bookie)}
            alt={bookie}
            style={{
              transform: "translateX(20px) translateY(100px) rotate(-90deg)",
              transformOrigin: "left center",
              borderRadius: "12px",
              width: "120px",
              height: "40px",
            }}
          ></img>
        ) : (
          <Typography
            sx={{
              transform: "translateX(20px) translateY(100px) rotate(-90deg)",
              transformOrigin: "left center",
              borderRadius: "12px",
              width: "120px",
              height: "40px",
              textAlign: "center",
              background: "#35BD5B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "black",
              ...textStyles,
            }}
          >
            {bookie}
          </Typography>
        )}
      </Box>
    );
  };

  const renderChip = (
    value,
    highlight = false,
    isPercentage = false,
    isAlreadyPercentage = false
  ) => {
    const formattedValue = isPercentage
      ? `+${((formatRate(value) - 1) * 100).toFixed(0)}%`
      : isAlreadyPercentage
      ? `${formatRate(value)}%`
      : formatRate(value);
    return (
      <Box
        sx={{
          width: "40px",
          height: "30px",
          position: "relative",
        }}
      >
        <Typography
          variant="subTitle1"
          sx={{
            borderRadius: "6px",
            width: "40px",
            height: "30px",
            backgroundColor: (theme) =>
              highlight ? "#35BD5B" : theme.palette.eventBackground.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    );
  };

  const renderBookieChip = (betDetails, bookie, i) => {
    const betForBookie = betDetails?.bets?.find((bet) => bet.bookie === bookie);

    if (!betForBookie) {
      return (
        <Box
          key={bookie}
          sx={{
            width: "40px",
            height: "30px",
            position: "relative",
          }}
        >
          <Typography
            variant="subTitle1"
            sx={{
              borderRadius: "6px",
              width: "40px",
              height: "30px",
              backgroundColor: (theme) =>
                formatRate(betDetails.maxOdds) ===
                formatRate(betForBookie?.rate)
                  ? "#35BD5B"
                  : theme.palette.eventBackground.main,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {formatRate(betForBookie?.rate)}
          </Typography>
        </Box>
      );
    }

    const betTrackerContent = getBetTrackerContent(betForBookie, betDetails);
    const showEVIndicator = isPro && betForBookie?.ev >= 3;
    return (
      <Tooltip
        key={i}
        placement="top"
        title={<BetTrackerButton content={betTrackerContent} />}
      >
        <Box
          key={bookie}
          sx={{
            width: "40px",
            height: "30px",
            cursor: "pointer",
            position: "relative",
          }}
          onClick={() => window.open(betForBookie?.source, "_blank")}
        >
          <Typography
            variant="subTitle1"
            sx={{
              borderRadius: "6px",
              width: "40px",
              height: "30px",
              backgroundColor: (theme) =>
                formatRate(betDetails.maxOdds) ===
                formatRate(betForBookie?.rate)
                  ? "#35BD5B"
                  : theme.palette.eventBackground.main,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            {formatRate(betForBookie?.rate)}
          </Typography>
          {showEVIndicator && (
            <Box
              sx={{
                position: "absolute",
                top: "-8px",
                right: "2px",
                width: "8px",
                height: "8px",
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: "8px", color: "#FFFFFF" }} />
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  // Check if any fair odds are greater than 0
  const hasFairOdds = useMemo(() => {
    return detailsRows.some((row) => row.fairOdds > 0);
  }, [detailsRows]);

  // Find the maximum maxAveRatio
  const maxMaxAveRatio = useMemo(() => {
    return Math.max(...detailsRows.map((row) => row.maxAveRatio));
  }, [detailsRows]);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          flexWrap: "nowrap",
        }}
      >
        <Box
          ref={topBoxRef}
          sx={{
            paddingX: "16px",
            mt: "120px",
            flex: 1,
            overflow: "scroll",
            maxHeight: "580px",
            minWidth: "min-content",
            maxWidth: { xs: "200px", sm: "250px" },
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "-ms-overflow-style": "none",
            scrollbarWidth: "none",
          }}
        >
          {detailsRows.map((betDetails) => (
            <Box
              key={betDetails.key}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "30px",
                marginTop: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <Typography>{betDetails.key}</Typography>

              <IconButton
                onClick={() => handleSort(betDetails)}
                sx={{ marginLeft: "auto" }}
              >
                <SortIcon />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box
          ref={bottomBoxRef}
          sx={{
            overflow: "auto",
            maxHeight: "700px",
            paddingRight: "8px",
            paddingBottom: "8px",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#0B2015",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
            scrollBehaviour: "smooth",
            scrollbarWidth: "thin",
            scrollbarColor: "#0B2015 #f1f1f1",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              position: "sticky",
              top: 0,
              zIndex: 1000,
              backgroundColor: (theme) => theme.palette.background.bookies,
            }}
          >
            {renderBookieLogo("Best Odds", "grey", false)}
            {renderBookieLogo("Avg Odds", "grey", false)}
            {renderBookieLogo("Best / Avg", "grey", false)}
            {hasFairOdds && renderBookieLogo("Fair Odds", "grey", false)}
            {isPro && <Box sx={{ marginRight: "40px" }}></Box>}

            {sortedSelectedUniqueBookies.map((bookie, i) =>
              renderBookieLogo(bookie, i)
            )}

            {sortedUnselectedUniqueBookies.map((bookie) =>
              renderBookieLogo(bookie)
            )}
          </Box>

          {detailsRows.map((betDetails, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "48px",
                mt: "10px",
              }}
            >
              {isPro && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {renderChip(betDetails?.maxOdds)}
                  {renderChip(betDetails?.aveOdds)}
                  {renderChip(
                    betDetails?.maxAveRatio,
                    betDetails.maxAveRatio === maxMaxAveRatio,
                    true
                  )}

                  {hasFairOdds && renderChip(betDetails?.fairOdds)}
                </Box>
              )}
              {!!sortedSelectedUniqueBookies?.length && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {sortedSelectedUniqueBookies.map((bookie, i) =>
                    renderBookieChip(betDetails, bookie, i)
                  )}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {sortedUnselectedUniqueBookies.map((bookie, i) =>
                  renderBookieChip(betDetails, bookie, i)
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BetDetails;
